import { CallbackGroupEntry } from './CallbackGroupEntry';
import { generateUuid } from './utils';

export class CallbackGroup {

    constructor() {
        /**
         * Underscore prefix used to suggested the variable is private.
         * @type {Object.<string, CallbackGroupEntry>}
         */
        this._entries = {};
    }

    /**
     * Triggers the execution of all callbacks stored in the group.
     * 
     * @param  {...any} args - Any arguments and data to send to the callbacks as parameters.
     * @returns {boolean} Whether or not any callbacks were successfully executed.
     */
    async invoke( ...args ) {
        const entries = Object.values( this._entries );
        if ( entries.length > 0 ) {
            await Promise.all( entries.map( ( entry ) => ( entry.invoke( ...args ) ) ) );
            return true;
        } else {
            return false;
        }
    }

    /**
     * Adds a callback to the group. Callbacks are assigned ids when added
     * to the group which is returned by this function when successfully
     * added. If the callback being added already exists, then it won't be
     * added again and the id of the existing callback will be returned. If
     * a callback is unable to be added for some reason, `undefined` will be
     * returned.
     * 
     * @param {function} callback - The callback to add to the event.
     * @returns {string|undefined} The id of the added callback or the callback that already existed.
     */
    add( callback ) {
        const existingEntry = this.getEntry( callback );
        if ( !existingEntry ) {
            // Generate new UUID for the new entry.
            const entryId = generateUuid();

            // Create the new entry and add it to entries object.
            const newEntry = new CallbackGroupEntry( entryId, callback );
            this._entries[ entryId ] = newEntry;

            // Return the UUID of the new entry added.
            return entryId;
        }

        // Return the UUID of the entry that already existed.
        return existingEntry.getId();
    }

    /**
     * Removes a callback from the group. If the callback is successfully
     * removed, the id of the callback is returned. If the callback doesn't
     * exist, `undefined` will be returned. 
     * 
     * @param {string} eventKey - The key of the event to remove the callback from.
     * @param {function|string} callback - The callback to remove from the event.
     * @returns {string|undefined} The id of the removed callback.
     */
    remove( callback ) {
        const existingEntry = this.getEntry( callback );
        if ( existingEntry ) {
            const key = existingEntry.getId();
            delete this._entries[ key ];
            return key;
        }
        return undefined;
    }

    /**
     * Returns the number of callbacks stored in the group.
     * 
     * @returns {number} The number of callbacks stored in the group
     */
    getCount() {
        return Object.keys( this._entries ).length;
    }

    /**
     * Returns the entry of a callback stored in the group. This callback
     * entry can be retrieved using either the callback itself, or the id
     * of the callback entry. If the entry can't be found, `undefined` will
     * be returned.
     * 
     * @param {string|function} identifier - Either the callback to look for, or the callbacks id.
     * @returns {EventSystemEntry} The entry if found. If not found, `undefined` will be returned.
     */
    getEntry( identifier ) {
        if ( typeof identifier === 'string' ) {
            return this._entries[ identifier ];
        }
        else if ( typeof identifier === 'function' ) {
            // Find the entries that have the callback being searched for.
            const matches = Object.values( this._entries ).filter( ( entry ) => {
                return entry.getCallback() === identifier;
            } );
            
            // If the callback to look for was found in any of the entries,
            // return the first entry that had the callback.
            if ( matches.length > 0 ) {
                return matches[ 0 ];
            }
        }

        return undefined;
    }

    /**
     * Returns whether or not the id or key of a callback entry exists.
     * 
     * @param {string} entryKey - The id or key of the callback entry to check the existence of.
     * @returns {boolean} Whether or not the id exists on an entry.
     */
    hasId( entryKey ) {
        return Boolean( this.getEntry( entryKey ) );
    }

    /**
     * Returns whether or not a callback exists in the group.
     * 
     * @param {string} entryKey - The callback to check the existence of.
     * @returns {boolean} Whether or not the callback exists in the group.
     */
    hasCallback( callback ) {
        return Boolean( this.getEntry( callback ) );
    }

}
